// ---------------------------------------------------------------------------
// Persisted identity. Follows the three-store convention from secret-hitler:
// gameStore is not persisted, playerStore and settingsStore are.
//
// TWO THINGS HERE ARE ABOUT COST RATHER THAN CORRECTNESS, and they compound.
//
// `reconnectTokens` and `drafts` are keyed by room code and nothing ever swept
// them, so a device that had played fifty games carried fifty rooms of dead
// tokens for the life of its localStorage. `persist` writes the WHOLE store on
// every `set`, and `useGameConnection.streamWord` sets a draft on every
// keystroke — so the per-letter cost of typing an answer was a serialization of
// every room the device had ever visited, on the main thread, mid-round. The
// two fixes are `MAX_TRACKED_ROOMS` and the buffered storage below.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StorageValue } from 'zustand/middleware';

/**
 * How many rooms this device remembers anything about.
 *
 * Eight, and the number is picked from what a token is FOR rather than from how
 * many rooms somebody has played. A reconnect token is only ever good for
 * rejoining a room that still exists, and a room is reclaimed ten minutes after
 * its last socket leaves — so nothing here is a history worth keeping. What has
 * to fit is the rooms this device could plausibly still hold a live seat in at
 * once, which is one per open tab, plus the handful it has just come from.
 * Eight is well above both and still bounds the persisted blob to a few hundred
 * bytes, whatever the device has been doing all evening.
 */
const MAX_TRACKED_ROOMS = 8;

interface PlayerState {
  displayName: string;
  /** Stable per-device id. Games are recorded against it so a guest can claim
   * them retroactively on their first sign-in. */
  deviceId: string;
  preferredColor: string | null;
  /** Keyed by room code, matching the existing convention. */
  reconnectTokens: Record<string, string>;
  /** Keyed by room code. Survives a reload mid-typing, so a restart during a
   * round loses nothing even if the last debounced sync did not land. */
  drafts: Record<string, string>;
  /**
   * Which rooms the two maps above are allowed to hold, oldest first.
   *
   * Recency has to be recorded rather than read off the maps: a room code may
   * be four digits — `ROOM_CODE_CHARSET` includes 2-9 — and an integer-like key
   * is sorted to the FRONT of an object's own key order by the language itself,
   * whenever it was inserted. Pruning by `Object.keys` order would therefore
   * have evicted a room called `2345` first, every time, including while
   * somebody was sitting in it.
   */
  roomOrder: string[];

  setDisplayName: (name: string) => void;
  setPreferredColor: (color: string | null) => void;
  setReconnectToken: (roomCode: string, token: string) => void;
  clearReconnectToken: (roomCode: string) => void;
  setDraft: (roomCode: string, draft: string) => void;
  clearDraft: (roomCode: string) => void;
}

function newDeviceId(): string {
  return crypto.randomUUID();
}

/** Whatever a map still holds about rooms that are still tracked. */
function keepTracked<T>(map: Record<string, T>, tracked: readonly string[]): Record<string, T> {
  const kept: Record<string, T> = {};
  for (const roomCode of tracked) {
    if (roomCode in map) kept[roomCode] = map[roomCode];
  }
  return kept;
}

/**
 * Marks a room as the most recently used one, and forgets everything about
 * whichever rooms fell off the far end.
 *
 * **The room being written can never be the one evicted.** It is appended LAST
 * and the slice keeps the TAIL, so it survives by construction — at a cap of
 * eight, and equally at a cap of one. That is the rule that matters here: a
 * reconnect token is what puts somebody back in the seat they are sitting in,
 * and every write about the room a player is currently in goes through this —
 * the token on join and on every reconnect, the draft on every keystroke — so
 * the current room is always the newest entry and never a candidate.
 */
function trackRoom(
  state: PlayerState,
  roomCode: string,
): Pick<PlayerState, 'roomOrder' | 'reconnectTokens' | 'drafts'> {
  const roomOrder = [...state.roomOrder.filter((code) => code !== roomCode), roomCode].slice(
    -MAX_TRACKED_ROOMS,
  );
  return {
    roomOrder,
    reconnectTokens: keepTracked(state.reconnectTokens, roomOrder),
    drafts: keepTracked(state.drafts, roomOrder),
  };
}

/** A room with nothing left to say about it stops holding one of the slots. */
function untrackIfEmpty(
  roomOrder: readonly string[],
  roomCode: string,
  reconnectTokens: Record<string, string>,
  drafts: Record<string, string>,
): string[] {
  if (roomCode in reconnectTokens || roomCode in drafts) return [...roomOrder];
  return roomOrder.filter((code) => code !== roomCode);
}

/**
 * How long a change may sit in memory before it reaches localStorage. Wider
 * than `DRAFT_DEBOUNCE_MS`, because this is not a sync anybody is waiting on:
 * nothing in the app reads its own persisted copy back until the next load.
 */
const WRITE_DELAY_MS = 500;

function storageOrNull(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Reading the property itself throws in some privacy modes.
    return null;
  }
}

let pendingWrite: { name: string; value: unknown } | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Put whatever is buffered out to localStorage now.
 *
 * Called by hand from the two token mutations — a token is what gets a player
 * back into their seat, so it is worth the synchronous write that a keystroke
 * is not — and from the page going away.
 */
export function flushPlayerStorage(): void {
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  const write = pendingWrite;
  pendingWrite = null;
  if (write === null) return;
  try {
    storageOrNull()?.setItem(write.name, JSON.stringify(write.value));
  } catch {
    // A full or unavailable store is not worth breaking a game over.
  }
}

/**
 * localStorage, written at most once every `WRITE_DELAY_MS`.
 *
 * `persist` calls `setItem` synchronously from every `set`, so without this a
 * keystroke serialized and wrote the entire store between one letter and the
 * next. Buffering costs nothing anybody can lose: what is held is always the
 * current state rather than a delta, `flushPlayerStorage` puts it out by hand
 * for the one field worth it, and the page going away flushes.
 *
 * This deliberately implements `PersistStorage` rather than wrapping
 * `createJSONStorage`, so the `JSON.stringify` is buffered along with the write
 * instead of happening on every `set` regardless.
 */
const bufferedStorage = {
  getItem: (name: string): StorageValue<PlayerState> | null => {
    // Anything still buffered is newer than what is on disk.
    if (pendingWrite !== null && pendingWrite.name === name) {
      return pendingWrite.value as StorageValue<PlayerState>;
    }
    const raw = storageOrNull()?.getItem(name) ?? null;
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as StorageValue<PlayerState>;
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: StorageValue<PlayerState>): void => {
    pendingWrite = { name, value };
    if (writeTimer === null) writeTimer = setTimeout(flushPlayerStorage, WRITE_DELAY_MS);
  },
  removeItem: (name: string): void => {
    if (writeTimer !== null) {
      clearTimeout(writeTimer);
      writeTimer = null;
    }
    pendingWrite = null;
    storageOrNull()?.removeItem(name);
  },
};

// `pagehide` is the reliable end of a page; `visibilitychange` is what a phone
// gets when the tab is backgrounded and may be killed without another event.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPlayerStorage);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flushPlayerStorage();
  });
}

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set) => ({
      displayName: '',
      deviceId: newDeviceId(),
      preferredColor: null,
      reconnectTokens: {},
      drafts: {},
      roomOrder: [],

      setDisplayName: (displayName) => set({ displayName }),
      setPreferredColor: (preferredColor) => set({ preferredColor }),

      setReconnectToken: (roomCode, token) => {
        set((state) => {
          const tracked = trackRoom(state, roomCode);
          return {
            ...tracked,
            reconnectTokens: { ...tracked.reconnectTokens, [roomCode]: token },
          };
        });
        flushPlayerStorage();
      },

      clearReconnectToken: (roomCode) => {
        set((state) => {
          const reconnectTokens = { ...state.reconnectTokens };
          delete reconnectTokens[roomCode];
          return {
            reconnectTokens,
            roomOrder: untrackIfEmpty(state.roomOrder, roomCode, reconnectTokens, state.drafts),
          };
        });
        flushPlayerStorage();
      },

      // The hot one: called on every keystroke of an answer. It buys its place
      // in the buffer above — no flush here, deliberately.
      setDraft: (roomCode, draft) =>
        set((state) => {
          const tracked = trackRoom(state, roomCode);
          return { ...tracked, drafts: { ...tracked.drafts, [roomCode]: draft } };
        }),

      clearDraft: (roomCode) =>
        set((state) => {
          const drafts = { ...state.drafts };
          delete drafts[roomCode];
          return {
            drafts,
            roomOrder: untrackIfEmpty(state.roomOrder, roomCode, state.reconnectTokens, drafts),
          };
        }),
    }),
    {
      name: 'countdown-player',
      version: 1,
      storage: bufferedStorage,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // A device that has never been seen needs an id before its first game.
        if (!state.deviceId) state.deviceId = newDeviceId();

        // Reconcile what was persisted before any of this existed — and what a
        // second tab may have written since — rather than letting the first
        // write after a load prune every room the order had never heard of.
        // Strays sort oldest, because nothing knows when they were last used.
        state.roomOrder ??= [];
        state.reconnectTokens ??= {};
        state.drafts ??= {};
        const known = new Set(state.roomOrder);
        const strays = [...Object.keys(state.reconnectTokens), ...Object.keys(state.drafts)].filter(
          (code) => !known.has(code),
        );
        const roomOrder = [...new Set([...strays, ...state.roomOrder])].slice(-MAX_TRACKED_ROOMS);
        state.roomOrder = roomOrder;
        state.reconnectTokens = keepTracked(state.reconnectTokens, roomOrder);
        state.drafts = keepTracked(state.drafts, roomOrder);
      },
    },
  ),
);

export { MAX_TRACKED_ROOMS };
